import { useParams } from 'react-router-dom';
import { BasePage } from '@/components/ui/base-page';
import { BreadcrumbNav, BreadcrumbItem } from '@/components/ui/breadcrumb-nav';
import { Play, ExternalLink, Clock, Calendar, Share2, Bookmark, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEffect, useState } from 'react';

// Sample podcast data
const mockPodcast = {
    name: 'future-of-investing',
    title: 'The Future of Investing in Digital Markets',
    description: 'Exploring the evolution of investment strategies in the age of digital assets and decentralized finance.',
    longDescription: 'Join our host Sarah Chen as she interviews leading experts in finance technology about the rapidly evolving landscape of digital investing. From blockchain-based assets to AI-powered trading strategies, discover how technology is reshaping the future of finance.',
    publishDate: 'May 20, 2023',
    duration: '45 min',
    hostName: 'Sarah Chen',
    hostTitle: 'Director of Research',
    hostImage: 'https://randomuser.me/api/portraits/women/54.jpg',
    coverImage: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=1000&q=80',
    spotifyUrl: 'https://open.spotify.com/episode/2RvgPVHiPJm9e5OxvaX9qN',
    tags: ['Investing', 'Digital Assets', 'Fintech', 'Blockchain']
};

// Sample related podcasts
const relatedPodcasts = [
    {
        id: '1',
        title: 'Digital Regulation: Navigating the New Landscape',
        duration: '38 min',
        publishDate: 'June 2, 2023',
        coverImage: 'https://images.unsplash.com/photo-1569025690938-a00729c9e1f9?auto=format&fit=crop&w=500&q=80',
        url: '/podcast/digital-regulation'
    },
    {
        id: '2',
        title: 'Crypto Winter: Lessons for Long-term Investors',
        duration: '42 min',
        publishDate: 'May 15, 2023',
        coverImage: 'https://images.unsplash.com/photo-1518546305927-5a555bb7020d?auto=format&fit=crop&w=500&q=80',
        url: '/podcast/crypto-winter'
    },
    {
        id: '3',
        title: 'Venture Capital in the Digital Age',
        duration: '51 min',
        publishDate: 'April 28, 2023',
        coverImage: 'https://images.unsplash.com/photo-1559589689-577aabd1db4f?auto=format&fit=crop&w=500&q=80',
        url: '/podcast/venture-capital-digital-age'
    },
    {
        id: '4',
        title: 'AI and the Future of Financial Analysis',
        duration: '35 min',
        publishDate: 'April 12, 2023',
        coverImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=500&q=80',
        url: '/podcast/ai-financial-analysis'
    }
];

export function PodcastPage() {
    const { name } = useParams<{ name: string }>();
    const [podcast, setPodcast] = useState(mockPodcast);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // Simulate data fetching
        setTimeout(() => {
            setIsLoading(false);
        }, 500);
    }, [name]);

    if (isLoading) {
        return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
    }

    const breadcrumbs: BreadcrumbItem[] = [
        { label: "Insights", href: "/insights" },
        { label: "Podcasts", href: "/insights/podcasts" },
        { label: podcast.title, href: `/podcast/${name}` }
    ];

    return (
        <BasePage title={podcast.title} description={podcast.description}>
            <div className="container mt-6 mb-4">
                <BreadcrumbNav items={breadcrumbs} />
            </div>

            {/* Desktop layout (hidden on mobile) */}
            <div className="hidden md:block container py-8">
                <div className="grid grid-cols-3 gap-8">
                    {/* Left column - Cover image and metadata */}
                    <div className="space-y-6">
                        <div className="aspect-square rounded-lg overflow-hidden shadow-lg">
                            <img
                                src={podcast.coverImage}
                                alt={podcast.title}
                                className="w-full h-full object-cover"
                            />
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Calendar size={16} />
                                    <span>{podcast.publishDate}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Clock size={16} />
                                    <span>{podcast.duration}</span>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                {podcast.tags.map(tag => (
                                    <span
                                        key={tag}
                                        className="px-3 py-1 bg-muted rounded-full text-xs font-medium"
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>

                            <div className="flex items-center gap-4">
                                <Button size="icon" variant="ghost">
                                    <Heart size={20} />
                                </Button>
                                <Button size="icon" variant="ghost">
                                    <Share2 size={20} />
                                </Button>
                                <Button size="icon" variant="ghost">
                                    <Bookmark size={20} />
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Middle and right columns - Content and related */}
                    <div className="col-span-2 space-y-8">
                        <div>
                            <h1 className="text-3xl font-bold mb-4">{podcast.title}</h1>
                            <p className="text-lg text-muted-foreground mb-6">{podcast.description}</p>
                            <p className="mb-8">{podcast.longDescription}</p>

                            <Button
                                className="mb-4"
                                size="lg"
                                onClick={() => window.open(podcast.spotifyUrl, '_blank')}
                            >
                                <Play size={18} className="mr-2" /> Listen on Spotify
                            </Button>

                            <div className="flex items-center p-4 bg-muted rounded-lg">
                                <div className="w-16 h-16 rounded-full overflow-hidden mr-4">
                                    <img
                                        src={podcast.hostImage}
                                        alt={podcast.hostName}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div>
                                    <h3 className="font-semibold">Hosted by {podcast.hostName}</h3>
                                    <p className="text-sm text-muted-foreground">{podcast.hostTitle}</p>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h2 className="text-xl font-semibold mb-4">Related Episodes</h2>
                            <div className="grid grid-cols-2 gap-4">
                                {relatedPodcasts.map(item => (
                                    <a href={item.url} key={item.id} className="group block">
                                        <div className="flex gap-3 p-3 rounded-lg border group-hover:border-primary transition-all">
                                            <div className="w-20 h-20 flex-shrink-0 rounded overflow-hidden">
                                                <img
                                                    src={item.coverImage}
                                                    alt={item.title}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="font-medium text-sm line-clamp-2 group-hover:text-primary transition-colors">{item.title}</h3>
                                                <div className="flex gap-3 mt-2 text-xs text-muted-foreground">
                                                    <span className="flex items-center gap-1">
                                                        <Clock size={12} />
                                                        {item.duration}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <Calendar size={12} />
                                                        {item.publishDate}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile layout (hidden on desktop) */}
            <div className="md:hidden">
                {/* Cover image and play button */}
                <div className="relative aspect-square">
                    <img
                        src={podcast.coverImage}
                        alt={podcast.title}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col justify-end p-4">
                        <h1 className="text-2xl font-bold text-white mb-2">{podcast.title}</h1>
                        <p className="text-white/80 text-sm mb-4">{podcast.description}</p>
                        <Button
                            className="w-full"
                            size="lg"
                            onClick={() => window.open(podcast.spotifyUrl, '_blank')}
                        >
                            <Play size={18} className="mr-2" /> Listen on Spotify
                        </Button>
                    </div>
                </div>

                {/* Podcast details */}
                <div className="container py-6 space-y-6">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar size={16} />
                            <span>{podcast.publishDate}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Clock size={16} />
                            <span>{podcast.duration}</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-4 justify-center py-2 border-y">
                        <Button size="icon" variant="ghost">
                            <Heart size={20} />
                        </Button>
                        <Button size="icon" variant="ghost">
                            <Share2 size={20} />
                        </Button>
                        <Button size="icon" variant="ghost">
                            <Bookmark size={20} />
                        </Button>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                        {podcast.tags.map(tag => (
                            <span
                                key={tag}
                                className="px-3 py-1 bg-muted rounded-full text-xs font-medium"
                            >
                                {tag}
                            </span>
                        ))}
                    </div>

                    <p className="text-sm">{podcast.longDescription}</p>

                    <div className="flex items-center p-4 bg-muted rounded-lg">
                        <div className="w-12 h-12 rounded-full overflow-hidden mr-3">
                            <img
                                src={podcast.hostImage}
                                alt={podcast.hostName}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div>
                            <h3 className="font-semibold text-sm">Hosted by {podcast.hostName}</h3>
                            <p className="text-xs text-muted-foreground">{podcast.hostTitle}</p>
                        </div>
                    </div>

                    <div>
                        <div className="flex justify-between items-center mb-3">
                            <h2 className="text-lg font-semibold">Related Episodes</h2>
                            <a href="/insights/podcasts" className="text-sm text-primary">See all</a>
                        </div>
                        <div className="overflow-x-auto pb-4">
                            <div className="flex gap-3" style={{ minWidth: 'max-content' }}>
                                {relatedPodcasts.map(item => (
                                    <a
                                        href={item.url}
                                        key={item.id}
                                        className="w-[160px] flex-shrink-0"
                                    >
                                        <div className="rounded-lg overflow-hidden aspect-square mb-2">
                                            <img
                                                src={item.coverImage}
                                                alt={item.title}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <h3 className="font-medium text-sm line-clamp-2">{item.title}</h3>
                                        <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                                            <span>{item.duration}</span>
                                            <span>{item.publishDate}</span>
                                        </div>
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </BasePage>
    );
} 